import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { UsageNudgeAudience } from "../../../types/nudges/NudgeDto";
import { FormattedDate, Tooltip } from "../../common";
import UsageNudgeComposeModal from "./UsageNudgeComposeModal";

interface UsageNudgeActionButtonProps {
  filters: AnalyticsFilterInputs | null;
  recipientEmployeeId: number;
  audience: UsageNudgeAudience;
  recipientName?: string;
  className?: string;
  actionLabel?: string;
  /** ISO timestamp of a previous nudge. When set, the button reads "Nudge again". */
  lastSentAt?: string | null;
  onSent?: () => void;
  onSendStarted?: () => void;
  onSendFailed?: () => void;
  onCooldown?: (lastSentAt: string, senderName: string | null) => void;
}

function UsageNudgeActionButton({
  filters,
  recipientEmployeeId,
  audience,
  recipientName,
  className,
  actionLabel,
  lastSentAt,
  onSent,
  onSendStarted,
  onSendFailed,
  onCooldown,
}: UsageNudgeActionButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const hasPreviousNudge = Boolean(lastSentAt);
  const label =
    actionLabel ??
    (hasPreviousNudge
      ? t("Analytics.Widgets.Usage.Nudge.ActionAgain", "Nudge again")
      : t("Analytics.Widgets.Usage.Nudge.Action", "Nudge"));

  const button = (
    <button
      type="button"
      className={
        className ??
        "btn-secondary btn-small inline-flex items-center gap-1.5 !text-[13px]"
      }
      onClick={(event) => {
        event.stopPropagation();
        setIsOpen(true);
      }}
    >
      <FontAwesomeIcon icon={faBell} aria-hidden />
      {label}
    </button>
  );

  return (
    <>
      {hasPreviousNudge && lastSentAt ? (
        <Tooltip
          triggerElement={button}
          content={t("Analytics.Widgets.Usage.Nudge.LastNudgedPrefix", "Last nudged")}
          htmlContent={
            <span className="inline-flex items-baseline gap-1 leading-snug text-white">
              <span>
                {t(
                  "Analytics.Widgets.Usage.Nudge.LastNudgedPrefix",
                  "Last nudged",
                )}
              </span>
              <FormattedDate
                date={new Date(lastSentAt)}
                displayMode="FROM-NOW"
                convertFromUtc
                className="pointer-events-none text-white"
              />
            </span>
          }
        />
      ) : (
        button
      )}

      <UsageNudgeComposeModal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        filters={filters}
        recipientEmployeeId={recipientEmployeeId}
        audience={audience}
        fallbackRecipientName={recipientName}
        onSent={onSent}
        onSendStarted={onSendStarted}
        onSendFailed={onSendFailed}
        onCooldown={onCooldown}
      />
    </>
  );
}

export default UsageNudgeActionButton;
